import { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineViewBoards } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/common/CustomButton";
import { SearchInput, SingleSelect } from "../../components/common/Search";
import CreateStrategyMapDrawer from "../../features/CreateStrategyMap/CreateStrategyMapDrawer";
import { SelectEntityDropdown } from "../../features/SelectEntity/SelectEntityDropdown";
import StrategyMapList from "../../features/StrategyMapsList/StrategyMapList";
import { useJwt } from "../../helpers/common";
import { useIntegrationStatus } from "../../hooks/useIntegrationStatus";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { ComponentPage } from "../../constants/constants";
import { SMapAction } from "../../redux/slices/strategyMap";
import { useOrganizationService } from "../../services";

import SortByDropdown from "../../components/common/SortByDropdown";
import { useNavigate } from "react-router";
import { OnboardingSmap } from "../../features/OnboardingGuide/StrategymapGuide/StrategymapGuide";

export function StrategyMapsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const JWTdata: any = useJwt();
  const org_id = JWTdata?.org_id;
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingData, setEditingData] = useState({}) as any;
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  // Filter States using centralized URL filters
  const { filters, setFilter, setFilters } = useUrlFilters([
    { urlKey: 'type', storageKey: 'smap_searchMode', defaultValue: 'name' },
    { urlKey: 'sort', storageKey: 'smap_sortBy', defaultValue: 'modified_at_desc' },
    { urlKey: 'source', storageKey: 'smap_sourceFilter', defaultValue: '' },
  ], ComponentPage.STRATEGY_MAPS);

  const searchMode = (filters.type as "name" | "epic" | "feature") || "name";
  const currentSearchType = searchMode === "name" ? "all" : searchMode;
  const sortBy = (filters.sort as string) || "modified_at_desc";
  const filterByStatus = (filters.source as string) || "";

  const [selectedEntities, setSelectedEntities] = useState<any[]>([]);

  const { isIntegrationActive } = useIntegrationStatus();
  const { fetchAllProjects, fetchMoreProjects } = useOrganizationService();

  const {
    loading,
    loadingMore,
    searchTerm,
    total,
    totalPages,
    page,
    smaps: data,
  } = useSelector((state: any) => state.levelMapList);

  // Determine if any filter is active beyond defaults
  const hasActiveFilters = useMemo(() => {
    if (searchMode !== 'name') return true;
    if (filterByStatus) return true;
    if (searchTerm) return true;
    return false;
  }, [searchMode, filterByStatus, searchTerm]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      type: 'name',
      sort: 'modified_at_desc',
      source: '',
    });
    dispatch(SMapAction.setSearchTerm(''));
    setSelectedEntities([]);
  }, [setFilters, dispatch]);

  // Initial project fetch
  useEffect(() => {
    if (org_id) {
      fetchAllProjects(org_id);
    }
  }, [org_id]);

  const createStrategyMap = () => {
    setEditingData({});
    setIsDrawerVisible(true);
  };

  const editStrategyMap = (data: any) => {
    setEditingData(data);
    setIsDrawerVisible(true);
  };

  // Filter Options
  const searchModeOptions = [
    { value: "name", label: "Strategy Map" },
    { value: "epic", label: "Epic" },
    { value: "feature", label: "Feature" },
  ];

  // Handlers
  const handleSearchChange = (value: any) => {
    dispatch(SMapAction.setSearchTerm(value.target.value));
  };

  const handleSearchModeChange = (option: any) => {
    setFilter('type', option.value);
    dispatch(SMapAction.setSearchTerm(""));
    setSelectedEntities([]);
  };

  const handleEntitiesChange = (entities: any[]) => {
    setSelectedEntities(entities);
    // If we select a single entity, we might want to set the search term to its name
    // For multiple, maybe join names or handle differently.
    // For now, let's just set the search term to the last selected one's name if it exists
    if (entities.length > 0) {
      const entities_uuid = entities.map((entity: any) => entity.id);
      dispatch(SMapAction.setSearchTerm(entities_uuid.join(",")));
    } else {
      dispatch(SMapAction.setSearchTerm(""));
    }
  };



  return (
    <div className="bg-light-surface dark:bg-tertiary-dark w-full relative h-screen flex flex-col overflow-hidden">
      <header className="flex-shrink-0 z-40 bg-light-header dark:bg-secondary-dark w-full border-b border-light-border-default dark:border-gray-700">
        <div className="px-6 py-4 flex items-center justify-between">
          <div
            className="flex items-center"
            data-onboarding-id="strategy-map-header"
          >
            <div className="flex flex-col">
              <h1 className="font-bold text-light-text-primary dark:text-white leading-tight text-3xl flex items-center mb-2">
                Strategy Maps
              </h1>
              <p className="text-light-text-secondary dark:text-slate-300 mb-0 pr-4 mt-0.5">
                Create, track, and visualize your Strategy Maps for
                effective project planning and execution.
              </p>
            </div>
          </div>

          <div
            className="flex items-center gap-3"
            data-onboarding-id="create-smap-btn"
          >
            <CustomButton
              onClick={createStrategyMap}
              leftIcon={
                <HiOutlineViewBoards className="text-xl text-white dark:text-white" />
              }
              className="inline-flex items-center justify-center font-medium rounded-md focus:outline-none transition-colors duration-200 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 dark:disabled:bg-blue-400 text-sm px-5 py-2.5 whitespace-nowrap bg-primary-light hover:bg-primary-hover px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 text-white"
            >
              <span>Create Strategy Map</span>
            </CustomButton>
          </div>
        </div>
      </header>

      {/* Filter Section */}
      <div
        className="mx-6 mt-6 p-4 rounded-xl border border-light-border-default dark:border-gray-700/50 bg-secondary-light dark:bg-secondary-dark flex gap-4 relative z-30 overflow-visible"
        data-onboarding-id="smap-filters"
      >
        {/* Search By */}
        <div className="w-[300px] flex-shrink-0">
          <label className="block text-xs font-medium text-gray-400 mb-1.5 ml-1">
            Search By
          </label>
          <SingleSelect
            name="searchMode"
            options={searchModeOptions}
            placeholder="Search by..."
            onChange={handleSearchModeChange}
            value={searchModeOptions.find(
              (opt) => opt.value === searchMode,
            )}
            width="w-full"
          />
        </div>

        {/* Map Name / Search Input */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-400 mb-1.5 ml-1">
            {searchMode === "name"
              ? "Map Name"
              : searchMode === "epic"
                ? "Epic Used"
                : "Feature Used"}
          </label>
          {searchMode === "name" ? (
            <SearchInput
              width="w-full"
              onChange={handleSearchChange}
              value={searchTerm}
              placeholder="Search Strategy Maps..."
            />
          ) : (
            <SelectEntityDropdown
              selectedEntities={selectedEntities}
              onChange={handleEntitiesChange}
              placeholder={
                searchMode === "epic"
                  ? "Select an Epic..."
                  : "Select a Feature..."
              }
              searchType={searchMode}
            />
          )}
        </div>
      </div>

      {/* Stats and Sort Section */}
      <div className="px-4 sm:px-6 md:px-8 py-3 flex items-center justify-between text-xs text-light-text-tertiary dark:text-gray-400">
        <div className="flex items-center gap-2">
          {loading && !loadingMore && (!data || data.length === 0) ? (
            <>
              <div className="h-4 w-44 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
              <span>•</span>
              <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            </>
          ) : (
            total > 0 && (
              <>
                <span>
                  Showing {data?.length || 0} of {total} items
                </span>
                <span>•</span>
                {totalPages > 1 && (
                  <span>
                    Page {page} of {totalPages}
                  </span>
                )}
              </>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium whitespace-nowrap transition-colors cursor-pointer hover:underline"
            >
              Clear Filters
            </button>
          )}
          <SortByDropdown
            value={sortBy}
            onChange={(e) => setFilter('sort', e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <StrategyMapList
          onEdit={editStrategyMap}
          onDelete={() => {}}
          onCreate={() => setIsDrawerVisible(true)}
          searchTerm={searchTerm}
          searchType={currentSearchType}
          sortMap={sortBy}
          source={filterByStatus}
          refreshKey={refreshKey}
        />
      </div>

      <CreateStrategyMapDrawer
        isVisible={isDrawerVisible}
        strategyMapId={editingData?.id ?? null}
        data={editingData}
        onSuccess={(id) => {
          setIsDrawerVisible(false);
          setRefreshKey((prev) => prev + 1);

          navigate(`/strategymaps/${id}`);
        }}
        onClose={() => {
          setIsDrawerVisible(false);
          // setRefreshKey((prev) => prev + 1);
        }}
      />

      <OnboardingSmap />
    </div>
  );
}
